import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { KeycloakWrapperService } from '@app/finxone-web-frontend/app/lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { dispatchNestedFormValue } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.utils';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { flattenObject } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import { ConfirmationHelperService } from '@app/finxone-web-frontend/app/lib/services/confirmation-helper/confirmation-helper.service';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-form-button-widget',
  template: `
    <div class="h-100">
      <finx-button
        [labeltext]="buttonLabel"
        class="pointer-events-none d-flex"
        [buttonStyle]="!isAllowedGlobalStyling ? contentStyle : {}"
        [class]="isAllowedGlobalStyling ? globalStylingClass : 'w-100'"
        [leftIcon]="leftIcon"
        [rightIcon]="rightIcon"
        [rightIconSize]="rightIconStyles"
        [leftIconSize]="leftIconStyles"
        (click)="handleClick()"
        [style.height]="'100%'"
        [class.mandatory-cta-button]="!isAllowedGlobalStyling"
        [isAllowedGlobalStyling]="isAllowedGlobalStyling"
      ></finx-button>
    </div>
  `,
})
export class FormButtonWidgetComponent extends BaseComponent implements OnInit {
  private readonly alertHandlerService = inject(AlertHandlerService);

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public leftIcon: string;
  public rightIcon: string;
  public leftIconStyles?: {
    width: string;
    height: string;
    color: string | undefined;
  };
  public rightIconStyles?: {
    width: string;
    height: string;
    color: string | undefined;
  };
  public marginStyle: baseCssConfigStyle;
  public globalStylingClass = '';
  public buttonLabel = '';
  public isAllowedGlobalStyling = false;

  constructor(
    private keycloakService: KeycloakWrapperService,
    private readonly configService: ConfigService,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private readonly store: Store,
    private readonly router: Router,
    private readonly iconCacheService: IconCacheService,
    private readonly countriesCurrenciesService: CountriesCurrenciesService,
    private readonly workflowFormsService: WorkflowFormsService,
    private readonly confirmationHelperService: ConfirmationHelperService,
    private readonly navigationService: NavigationService,
  ) {
    super();
  }

  ngOnInit() {
    this.onLoadComponent();
  }

  private onLoadComponent() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      const labelOfButton = this.attri.widgetProperties['textContent'];
      const template = Handlebars.compile(labelOfButton);

      buildHandleBarModel(this.store, labelOfButton, this.countriesCurrenciesService, this.iconCacheService)
        .pipe(takeUntil(this.destroy$))
        .subscribe((model) => {
          this.buttonLabel = filterIfHandlebarNotRendered(template(model));
        });
      this.leftIconStyles = {
        width:
          (this.attri.css_config['lefticonSize'] ?? 20) + (this.attri.css_config['leftIconUnit'] ?? 'px'),
        height:
          (this.attri.css_config['lefticonSize'] ?? 20) + (this.attri.css_config['leftIconUnit'] ?? 'px'),
        color: this.attri.css_config['leftIconColor'],
      };
      this.rightIconStyles = {
        width:
          (this.attri.css_config['righticonSize'] ?? 20) + (this.attri.css_config['rightIconUnit'] ?? 'px'),
        height:
          (this.attri.css_config['righticonSize'] ?? 20) + (this.attri.css_config['rightIconUnit'] ?? 'px'),
        color: this.attri.css_config['iconColor'] ?? this.attri.css_config['rightIconColor'],
      };

      this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        ...commonContentStyleList,
        ...commonTextContentStyleList,
      ]);

      if (this.attri.widgetProperties?.lefticon || this.attri.widgetProperties?.righticon) {
        this.leftIcon = this.attri.widgetProperties?.lefticon as string;
        this.rightIcon = this.attri.widgetProperties?.righticon as string;
      }
    }
    this.getGlobalStyling();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }
  }

  public handleClick() {
    const isSubmitTrue = !!(this.dataIn?.attri?.widgetProperties?.['formButtonSubmissionType'] === 'submit');
    this.performButtonClickAction(isSubmitTrue);
  }

  private performButtonClickAction(isSubmitTrue: boolean) {
    const currentPageForms = this.workflowFormsService.getCurrentPageForms();
    const allCurrentPageFormsValid = currentPageForms.every((formId) =>
      this.workflowFormsService.isFormValid(formId),
    );
    if (!allCurrentPageFormsValid) {
      this.workflowFormsService.markCurrentPageFormsTouched();
      const formValues = flattenObject(this.readCurrentPageFormsValues(currentPageForms));

      const hasEmailKey = formValues && 'isEmailVerified' in formValues;
      const isEmailVerified = formValues?.['isEmailVerified'];

      const hasPhoneKey = formValues && 'isPhoneVerified' in formValues;
      const isPhoneVerified = formValues?.['isPhoneVerified'];

      if ((hasEmailKey && !isEmailVerified) || (hasPhoneKey && !isPhoneVerified)) {
        this.alertHandlerService.showAlertFn('error', 'Email or phone not verified.');
        console.warn('Email or phone not verified.');
      }
      return;
    }
    const formValues = this.readCurrentPageFormsValues(currentPageForms);
    Object.keys(formValues).forEach((formId) => {
      this.dispatchFormValueForPath(formId, formValues[formId] || {});
    });

    if (isSubmitTrue) {
      console.info('Form submission triggered');
      // Logic to handle form submission will go here
    }

    if (this.dataIn.attri?.['zone']?.includes('login')) {
      this.keycloakService.loginLink({
        redirectUri: globalThis.location.origin + '/post-auth',
      });
    } else {
      if (!this.confirmationHelperService.reconfirmationConfig()) {
        const { pageUrl } = this.navigationService.buildPageNavigationUrl({
          pageId: this.dataIn.attri?.['pageId'] || '',
          zone: this.dataIn.attri?.['zone'],
          pageUrl: this.dataIn.attri?.['pageUrl'],
          role: this.dataIn.attri?.['role'],
          navigationOption: this.dataIn.attri?.['navigationOption'],
          tabRef: this.dataIn.attri?.['tabRef'],
          pageTransition: this.dataIn.attri?.['pageTransition'] || 'forward',
        });

        this.navigationService.navigateByPageUrl({
          url: pageUrl,
          targetRole: this.dataIn.attri?.['role'],
          currentRole: this.store.selectSnapshot(RoleState.getRole)?.role,
          zone: this.dataIn.attri?.['zone'],
          externalLink: this.dataIn.attri?.['externalUrl'],
          browserBehaviour: this.dataIn.attri?.['browserBehaviour'],
        });
      } else {
        const url = this.confirmationHelperService.reconfirmationConfig()?.redirectUrl;
        this.confirmationHelperService.clearReconfirmationConfig();
        this.router.navigateByUrl(url || '');
      }
    }
  }

  private readCurrentPageFormsValues(currentPageForms: string[]): Record<string, any> {
    const formValues: Record<string, any> = {};
    currentPageForms.forEach((formId) => {
      const formValue = this.workflowFormsService.getFormValues(formId);
      if (formValue) {
        formValues[formId] = formValue;
      }
    });
    return formValues;
  }

  /**
   * currentPageForms entries are resolved WorkflowFormsService paths, not FormActionState ids.
   * A 'group'/'array' Form Widget registers where it nests (rootFormId/childKey/arrayIndex) via
   * registerCurrentPageFormWidgetForm when it resolves its FormGroup - reuse that here instead of
   * re-deriving structure from the path string, which can't distinguish "childKey that happens to
   * look numeric" from "array index" and breaks silently if the path shape ever changes.
   * Legacy direct createForm() callers (e.g. card-name-widget, beneficiary-form.service) never
   * register nesting metadata, so they fall back to the original flat dispatch onto formId as-is.
   */
  private dispatchFormValueForPath(formId: string, formValue: Record<string, unknown>): void {
    const nestedFormMeta = this.workflowFormsService.getNestedFormDispatchMeta(formId);
    dispatchNestedFormValue(this.store, nestedFormMeta ?? { rootFormId: formId }, formValue);
  }
}
